"use client";

import { useResponsive } from "@dafiti/closet-ui/hooks";
import { usePathname } from "next/navigation";
import { useMemo } from "react";

import type { DefaultBooleanFacet, Facet } from "../types/filters.type";
import { useFiltersQuery } from "./useFiltersQuery.hook";

export function useQuickFilters() {
  const { facets, isLoading } = useFiltersQuery();
  const { isMobile } = useResponsive();
  const pathname = usePathname();

  const { booleanFilters, dropdownFilters } = useMemo(() => {
    const quickFacets = facets
      .filter((f) => f.is_quickfilter)
      .filter((f) => !(f.field === "brand" && pathname.startsWith("/b/")));

    const booleans = quickFacets
      .filter(
        (f): f is DefaultBooleanFacet =>
          f.component === "Default" && f.selector === "Boolean",
      )
      .sort((a, b) => a.order - b.order);

    const nonBooleans = quickFacets
      .filter(
        (f): f is Facet =>
          !(f.component === "Default" && f.selector === "Boolean"),
      )
      .sort((a, b) => a.order - b.order);

    // Mobile only: active filters bubble to the front while preserving relative order
    // within each group (filter() is stable). Desktop keeps BFF order.
    const dropdowns = isMobile
      ? [
          ...nonBooleans.filter((f) => f.is_selected),
          ...nonBooleans.filter((f) => !f.is_selected),
        ]
      : nonBooleans;

    return { booleanFilters: booleans, dropdownFilters: dropdowns };
  }, [facets, isMobile, pathname]);

  return { booleanFilters, dropdownFilters, isLoading };
}
